import { apiClient } from '@/lib/api';
import type { PaginaDelHistorial } from '@/types/historial.types';
import { conCambioPropio } from '../lib/cambio-propio';
import { conSoloEjemplos } from '@/features/mini-prixia/solo-ejemplos';
import type {
  AppliedStrategy,
  Cadence,
  CreateAppliedStrategy,
  CreateResult,
  EstrategiaEnBorradorBody,
  GroupDraft,
  OverlapPreview,
  EstadoEnLaEstrategia,
  PaginaDelAlcance,
  ProductoElegible,
  ProductosDeLaEstrategia,
  ProductoEnBorrador,
  ProductoEnBorradorBody,
  RunSummary,
  SimulacionDeEstrategia,
  StrategyMode,
} from '../types/strategy-applications.types';

/** Estrategias aplicadas — Etapa 4, Tarea 7 (plan 167 §7). */
export const strategyApplicationsApi = {
  listar: async (): Promise<AppliedStrategy[]> =>
    apiClient.get<AppliedStrategy[]>('/api/strategy-applications'),

  /** Lote B2 (punto 6): todos los productos que alcanza hoy, con cómo está cada uno. */
  productosDeLaEstrategia: (
    id: string,
    f: { q?: string; estado?: EstadoEnLaEstrategia; page?: number } = {}
  ): Promise<ProductosDeLaEstrategia> => {
    const qs = new URLSearchParams();
    if (f.q) qs.set('q', f.q);
    if (f.estado) qs.set('estado', f.estado);
    if (f.page && f.page > 1) qs.set('page', String(f.page));
    const s = qs.toString();
    return apiClient.get<ProductosDeLaEstrategia>(
      `/api/strategy-applications/${id}/productos${s ? `?${s}` : ''}`
    );
  },

  /**
   * Spec 294 (D-R4-5): el historial de cambios, de a 20. `antesDe` = el
   * `siguiente` de la página anterior (null = la primera).
   */
  historial: (id: string, antesDe: number | null): Promise<PaginaDelHistorial> =>
    apiClient.get<PaginaDelHistorial>(
      `/api/strategy-applications/${id}/historial${antesDe != null ? `?antesDe=${antesDe}` : ''}`
    ),

  /** A quién alcanzaría, y con qué estrategia choca. Se pide ANTES de crear. */
  vistaPrevia: async (group: GroupDraft, excludeApplicationId?: string): Promise<OverlapPreview> =>
    apiClient.post<OverlapPreview>('/api/strategy-applications/preview', {
      group,
      ...(excludeApplicationId ? { excludeApplicationId } : {}),
    }),

  /**
   * Los productos que un alcance agarraría, antes de crear nada (plan 1, T1). Es
   * otra pregunta que `productos()` de abajo, que busca entre todo el catálogo
   * para elegir a mano.
   */
  productosDelAlcance: (
    group: GroupDraft,
    page = 1,
    limit = 20,
    /** Plan 3, P3-D12: nombre o código, dentro del alcance. */
    q?: string,
    /** Agregados del 29-sep (A2): sólo los que ya tiene otra estrategia. */
    opts: { soloConOtra?: boolean } = {}
  ): Promise<PaginaDelAlcance> =>
    apiClient.post<PaginaDelAlcance>('/api/strategy-applications/productos', {
      group,
      page,
      limit,
      ...(q?.trim() && { q: q.trim() }),
      ...(opts.soloConOtra && { soloConOtra: true }),
    }),

  crear: async (body: CreateAppliedStrategy): Promise<CreateResult> =>
    apiClient.post<CreateResult>('/api/strategy-applications', body),

  cambiar: async (
    id: string,
    cambios: {
      isActive?: boolean;
      mode?: StrategyMode;
      askAboveChangePct?: number | null;
      cadence?: Cadence;
      cadenceHour?: number | null;
      cadenceMinute?: number;
      cadenceDayOfWeek?: number | null;
    }
  ): Promise<{
    id: string;
    isActive: boolean;
    nextRunAt: string | null;
    /** Spec 294: cuántas se retiraron de Por decidir al apagarla. */
    retiradas?: number;
    seDetuvoUnaCorrida?: boolean;
  }> =>
    // Spec 294: una acción propia, la franja «Hay cambios nuevos» no avisa por ella.
    conCambioPropio(apiClient.patch('/api/strategy-applications/' + id, cambios)),

  /**
   * «Si hoy corriera» y las reglas que la alcanzan, sin crearla (4B-datos T10).
   * Pasa por el limitador de escrituras: la pantalla espera ≥ 400 ms tras tipear.
   */
  simular: (body: EstrategiaEnBorradorBody): Promise<SimulacionDeEstrategia> =>
    apiClient.post<SimulacionDeEstrategia>('/api/strategy-applications/simular', body),

  /**
   * Un producto con la estrategia en borrador (agregados del 29-sep, A4): la
   * fila abierta de «Resultados esperados», con sus tiendas. Se pide al abrirla.
   */
  simularProducto: (body: ProductoEnBorradorBody): Promise<ProductoEnBorrador> =>
    apiClient.post<ProductoEnBorrador>('/api/strategy-applications/simular/producto', body),

  /** Revisión final de rama (F9): acción propia, la franja no avisa por ella. */
  // Ronda del QA de R4: `retiradas`, lo que la baja retiró de Por decidir (ausente = servidor viejo).
  darDeBaja: async (id: string): Promise<{ id: string; retiradas?: number }> =>
    conCambioPropio(
      apiClient.delete<{ id: string; retiradas?: number }>('/api/strategy-applications/' + id)
    ),

  /**
   * Correr ahora. Corre en linea y puede tardar, porque recorre los productos
   * sucios del grupo: el timeout por omision no alcanza. Spec 275 (R3): es una
   * acción propia, la franja «Hay cambios nuevos» no avisa por ella.
   */
  correrAhora: async (id: string): Promise<RunSummary> =>
    conCambioPropio(
      apiClient.post<RunSummary>(`/api/strategy-applications/${id}/run`, undefined, {
        timeout: 180000,
      })
    ),

  /**
   * Los productos entre los que se puede elegir para un grupo manual, buscados
   * en el servidor. El endpoint devuelve como mucho 100 por página (pedir 500 no
   * trae más), así que se busca allá y se devuelve cuántos hay de verdad.
   */
  productos: async (busqueda = ''): Promise<{ productos: ProductoElegible[]; total: number }> => {
    const params = new URLSearchParams({ limit: '100', isActive: 'true' });
    const q = busqueda.trim();
    if (q) params.set('search', q);
    const r = await apiClient.get<{ products: ProductoElegible[]; total: number }>(
      // Reglas de la demo (343, S3.4): en la prueba, el grupo manual se elige entre los ejemplos.
      conSoloEjemplos(`/api/dynamic-pricing/products?${params.toString()}`)
    );
    const productos = r.products ?? [];
    return { productos, total: r.total ?? productos.length };
  },
};

export default strategyApplicationsApi;
